<template>
  <div v-if="!loading && pages.length && !_.get(pages, '0.items', []).length" :class="['w-full sm:w-fit', isMain ? 'pt-5 px-5 mt-5' : '']">
    <h1 class="flex font-semibold text-xl text-heading">
      {{ title }}
    </h1>
    <div v-if="routeCategory.idCategory.value" class="text-sm text-sub-heading mt-2 mb-6">
      {{ _.find(flatCategories, { id: routeCategory.idCategory.value })?.content }}
    </div>
    <FeedFilterTags v-if="isMain" :is-detail="isDetail" />
    <div v-if="!idSupplier && !routeName.startsWith('search___')" class="w-full md:w-156 2xl:w-239 flex flex-col justify-center ml-1">
      <NotFound v-if="!isFavorite" text="text-no-filtered-found" class="max-w-lg mx-auto" />
    </div>
  </div>
  <div v-else id="productsView" class="w-full sm:w-fit px-5">
    <div v-if="!label" class="mt-10 mb-5 w-full sm:w-156 2xl:w-239">
      <h1 class="flex font-semibold text-xl text-heading">
        {{ title }}
      </h1>
      <div v-if="routeCategory.idCategory.value" class="text-sm text-sub-heading mt-2">
        {{ _.find(flatCategories, { id: routeCategory.idCategory.value })?.content }}
      </div>
    </div>
    <FeedFilterTags :is-detail="isDetail" :class="[ 'px-0', isMain ? 'mt-6' : '' ]" />
    <div v-if="!routeCategory.idPostCategory.value" :class="[ 'pb-20' ]">
      <h1 v-if="label && loaded" class="flex font-semibold text-xl text-heading mb-5">
        {{ title }}
      </h1>
      <div v-if="!loaded || (loading && !filters.fulltext)" class="w-full sm:w-fit flex justify-center">
        <div class="w-full sm:w-fit grid md:grid-cols-2 2xl:grid-cols-3 gap-x-9 gap-y-2.5 sm:gap-y-9">
          <ProductFeedLoader :limit="6" />
        </div>
      </div>
      <div class="w-full sm:w-fit flex flex-col justify-center">
        <div class="w-full sm:w-fit grid md:grid-cols-2 2xl:grid-cols-3 gap-x-9 gap-y-2.5 sm:gap-y-9">
          <RenderProductCard
              v-for="(variant, idx) in variants"
              :key="variant.id"
              :variant="variant"
              :class="loaded ? '' : 'hidden'"
              :class-name="_.get(variant, 'product.status', STATUS.DRAFT) !== STATUS.ACTIVE ? 'opacity-50' : ''"
              :loading="idx < 3 ? null : 'lazy'"
          />
        </div>
      </div>
      <div v-if="hasNextPage || loadingMore" class="flex justify-center mt-8 lg:mt-12">
        <Button
            :variant="isDetail ? 'outline' : 'outlinePanel'"
            :loading="loadingMore"
            class-name="text-sm md:text-base font-semibold h-11"
            @click="handleLoadMore"
        >
            {{ $t("text-load-more") }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { PRODUCT_SUBTYPE, STATUS } from "@utils/constants";
import { sitePages } from "@settings/site-pages.settings";
import { useSelectedStore } from '@stores/selected'
import { useEnumStore } from '@stores/enum';
import { useSearchStore } from '@stores/search';
import { useContentStore } from '@stores/content';
import { useCategoryStore } from '@stores/category';
import ProductFeedLoader from "@components/ui/loaders/product-feed-loader";
import RenderProductCard from "@components/product/render-product-card";
import Button from "@components/ui/button";
import NotFound from "@components/common/not-found";
import FeedFilterTags from "@components/filter/feed/feed-filter-tags";

const props = defineProps({
  productType: {
    type: String,
    default: null
  },
  idCategory: {
    type: Number,
    default: null
  },
  idSupplier: {
    type: Number,
    default: null
  },
  isFavorite: {
    type: Boolean,
    default: null
  },
  isMain: {
    type: Boolean,
    default: true
  },
  isDetail: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ''
  }
});

const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();

const enums = useEnumStore();
const store = useSelectedStore();
const search = useSearchStore();
const contents = useContentStore();
const categories = useCategoryStore();
const route = useRoute();
const routeLang = useRouteLang();
const config = useRuntimeConfig();

const category = useState('route.category', () => config.public.layoutDesign);
const geolocation = useGeolocation();
const routeCategory = useRouteCategory();

const flatCategories = computed(() => _.concat(categories.eshop, _.flatMap(categories.eshop, (item) => _.map(item?.categories || [], (cat) => _.assignIn(cat, { idCategory: item.id }))) || []));

const title = computed(() => {
  const parts = [];

  if (filters.value.fulltext) {
    return `${filters.value.fulltext} ${t(props.label).toLocaleLowerCase()}`;
  }

  if (routeCategory.idCategory.value) {
    const idCategory = _.find(flatCategories.value, { id: routeCategory.idCategory.value })?.idCategory;
    if (idCategory) {
      parts.push(_.find(flatCategories.value, { id: idCategory })?.name);
      parts.push(sitePages[config.public.layoutDesign].titleSeparator + _.find(flatCategories.value, { id: routeCategory.idCategory.value })?.name.toLowerCase());
    } else {
      parts.push(_.find(flatCategories.value, { id: routeCategory.idCategory.value })?.name);
    }
    parts.push(t(props.label ? props.label : 'text-ads').toLowerCase());
  } else {
    parts.push(t(props.label ? props.label : 'text-all-ads'));
  }

  return _.join(parts, ' ');
});

const { filters } = useVariantFilters(props);

const isPrivate = props.idSupplier || props.isFavorite;
const subtype = PRODUCT_SUBTYPE[props.productType || (props.isFavorite ? PRODUCT_SUBTYPE.favorite : null) || (props.idCategory ? PRODUCT_SUBTYPE.category : null) || (props.idSupplier ? PRODUCT_SUBTYPE.supplier : null) || PRODUCT_SUBTYPE.ads] || PRODUCT_SUBTYPE.category;
const bodyFilters = useState(`products.${subtype}.filters.${category.value}`, () => {});
const pages = isPrivate
  ? ref([])
  : useState(`products.${subtype}.pages.${category.value}`, () => []);
const pagination = isPrivate
  ? ref({ page: 1, pageCount: 1, itemsPerPage: 18, itemCount: 0 })
  : useState(`products.${subtype}.pagination.${category.value}`, () => ({ page: 1, pageCount: 1, itemsPerPage: 18, itemCount: 0 }));
const loading = ref(false);
const loaded = ref(search.variantsLoaded);
const hasNextPage = computed(() => pagination.value.page < pagination.value.pageCount);
const loadingMore = ref(false);
const replacePages = ref(false);

const variants = computed(() => loading.value && !pages.value.length
  ? []
  : _.flatMap(pages.value, (page) => page?.items || [])
);

const reloadFeed = async (pending = loading) => {
  pending.value = true;
  $eventBus.emit('feed:filters:update', filters.value);

  const body = _.pickBy(Object.assign(
    {},
    _.omit(filters.value, ['sort', 'direction', 'location', 'maxKmFromHome', 'maxKmFromLocation']),
    !store.hasCurrentLocation() && filters.value.maxKmFromHome ? {
      maxKmFromHome: filters.value.maxKmFromHome
    } : {},
    store.hasCurrentLocation() ? {
      currentLocationLat: +(geolocation.coordinates.value.latitude || store.coordinates.latitude),
      currentLocationLng: +(geolocation.coordinates.value.longitude || store.coordinates.longitude)
    } : {},
    store.hasCurrentLocation() && filters.value.maxKmFromLocation ? {
      maxKmFromLocation: filters.value.maxKmFromLocation
    } : {},
  ), value => value !== null && value !== '' && value !== 0);

  if (JSON.stringify(bodyFilters.value) !== JSON.stringify(body)) {
    replacePages.value = true;
    pagination.value.page = 1;
  }

  const { status } = await loadProductVariants(
    {
      page: pagination.value.page,
      limit: pagination.value.itemsPerPage,
      sort: filters.value?.sort || 'publishedAt',
      direction: filters.value?.direction || 'desc',
      locale: routeLang.locale.value
    },
    body
  );

  if (status.value === 'error') {
    if (import.meta.client) {
      $toast.error(t('error.product-list.load'));
    }
  } else {
    const data = search.popVariants();
    bodyFilters.value = body;
    if (replacePages.value) {
      pages.value = [ data.value ];
    } else {
      pages.value.push(data.value);
    }
    replacePages.value = false;
    pagination.value = data.value.pagination;
  }
  pending.value = false;
  loaded.value = true;

  if (store.action === 'OPEN_FILTER_VIEW') {
    nextTick(() => {
      store.setAction(null);
      $eventBus.emit('sidebar:open', 'FILTER_VIEW');
    });
  }

  if (import.meta.client && filters.value?.fulltext) {
    scrollToView();
  }
}

const reload = import.meta.client ? _.debounce(reloadFeed, 500, { leading: false, trailing: true }) : reloadFeed;

const handleLoadMore = async () => {
  pagination.value.page++;
  await reload(loadingMore);
}

const routeName = computed(() => route?.name || '');

watch(() => geolocation.coordinates.value, async () => {
  if (import.meta.server || navigator?.onLine) {
    replacePages.value = true;
    pagination.value.page = 1;
    nextTick(async () => await reload());
  }
}, { deep: true });

watch(() => enums.eshopParameters, async () => {
  filters.value.parameters = transformEshopParameters(route.query);
  if (!_.isEmpty(filters.value.parameters) && (import.meta.server || navigator?.onLine)) {
    replacePages.value = true;
    pagination.value.page = 1;
    await reload();
  }
});

watch(() => store.loadReset, async values => {
  if (values[subtype] && (import.meta.server || navigator?.onLine)) {
    store.setLoadReset(subtype, false);
    replacePages.value = true;
    pagination.value.page = 1;
    await reload();
  }
}, { deep: true });

watch(() => route.query, async values => {
  if (loading.value || loadingMore.value) {
    return;
  }
  filters.value = {
    type: props.productType,
    idSupplier: +(props.idSupplier || route.query.idSupplier || null),
    idCategory: +(props.idCategory || route.query.idCategory || null),
    isFavorite: props.isFavorite,
    fulltext: route.query?.fulltext || null,
    location: route.query?.location || null,
    maxKmFromHome: route.query?.maxKmFromHome && !route.query?.maxKmFromLocation ? +route.query?.maxKmFromHome : null,
    maxKmFromLocation: route.query?.maxKmFromLocation ? +route.query?.maxKmFromLocation : null,
    parameters: transformEshopParameters(values),
    sort: values?.sort || 'publishedAt',
    direction: values?.direction || 'desc'
  };
  if (import.meta.server || navigator?.onLine) {
    replacePages.value = true;
    pagination.value.page = 1;
    await reload();
  }
});

onServerPrefetch(async () => {
  pagination.value.page = 1;
  pages.value = [contents.variants];
});

const variantUpdate = (value) => {
  const variant = _.find(variants.value, { id: value.id });
  if (variant) {
    _.assignIn(variant, value);
    store.setLoadResetOthers();
  }
};

const feedReload = () => {
  if (import.meta.server || navigator?.onLine) {
    replacePages.value = true;
    pagination.value.page = 1;
    geolocation.withLocation(reload);
  }
};

onMounted(async () => {
  if (!_.isEmpty(pages.value) || store.loadReset[subtype]) {
    store.setLoadReset(subtype, false);
  }
  if (import.meta.server || navigator?.onLine) {
    replacePages.value = true;
    pagination.value.page = 1;
    if (!store.hasCurrentLocation() && !route.query?.latitude && !route.query?.longitude && !route.query?.fulltext) {
      geolocation.withLocation(reload);
      store.setLoadReset(PRODUCT_SUBTYPE.main, true);
    } else {
      await reload();
    }
  }
  $eventBus.on('variant:update', variantUpdate);
  $eventBus.on('feed:reload', feedReload);
});

onUnmounted(() => {
  $eventBus.off('variant:update', variantUpdate);
  $eventBus.off('feed:reload', feedReload);
});

</script>
